/**
 * Demand By Day Stacked Chart
 *
 * Displays demand forecast as a stacked bar chart
 * with product types (strains) stacked per day.
 * Supports configurable time periods via period/offset props.
 */

import { useState, useEffect } from 'react'
import { toLocalDateString, parseDateString } from '../../utils/dateUtils'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts'
import { getForecastByPeriod, getDateRangeForPeriod } from '../../services/forecastService'
import { supabase } from '../../supabase-client'

// Product colours - consistent palette
const PRODUCT_COLOURS = {
  "Lion's Mane": '#F54A00',
  'Grey Oyster': '#009689',
  'Coral Tooth': '#6366F1',
  'Pink Oyster': '#EC4899',
  'Yellow Oyster': '#F59E0B',
  'King Oyster': '#8B5CF6',
  'Shiitake': '#10B981',
  'Chestnut': '#78716C'
}

// Fallback colours for any other products
const FALLBACK_COLOURS = [
  '#FF6B2C', '#00B8A9', '#818CF8', '#F472B6',
  '#FBBF24', '#A78BFA', '#34D399', '#A8A29E'
]

// Custom tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0)
    return (
      <div style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <p className="text-sm font-medium mb-2">{label}</p>
        <div className="space-y-1">
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 text-xs">
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-sm"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-muted-foreground">{entry.name}</span>
              </div>
              <span className="font-medium">{entry.value.toFixed(1)}kg</span>
            </div>
          ))}
          <div className="border-t border-border pt-1 mt-1 flex justify-between text-xs font-medium">
            <span>Total</span>
            <span>{total.toFixed(1)}kg</span>
          </div>
        </div>
      </div>
    )
  }
  return null
}

export default function DemandByDayStackedChart({
  period = '2weeks',
  offset = 0,
  loading: externalLoading
}) {
  const [data, setData] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [dataType, setDataType] = useState('predicted') // 'predicted' or 'historical'

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true)

        // Get date range for the selected period
        const { startDate, endDate, comparisonStart, comparisonEnd } = getDateRangeForPeriod(period, offset)

        // Fetch predictions for the date range
        const { data: predictions, error: predError } = await supabase
          .from('sale_suggestions')
          .select(`
            suggested_date,
            suggested_quantity,
            product:products(strain)
          `)
          .eq('status', 'pending')
          .gte('suggested_date', startDate)
          .lte('suggested_date', endDate)
          .order('suggested_date', { ascending: true })

        if (predError) throw predError

        // Check if we have predictions covering the period
        const hasPredictions = predictions && predictions.length > 0

        // For longer periods (month+), also fetch historical data as baseline
        const needsHistorical = ['month', '3months', 'quarter', 'year'].includes(period)

        let historicalData = []
        if (needsHistorical) {
          const { data: historical, error: histError } = await supabase
            .from('sales')
            .select(`
              date,
              sale_items(
                quantity,
                product:products(strain)
              )
            `)
            .gte('date', comparisonStart)
            .lte('date', comparisonEnd)
            .order('date', { ascending: true })

          if (!histError) {
            historicalData = historical || []
          }
        }

        // Build data structure
        const allProducts = new Set()
        const byDateAndProduct = {}
        const isLongPeriod = ['month', '3months', 'quarter', 'year'].includes(period)

        // For long periods, we need to show both predictions AND historical baseline
        // Aggregate by week for cleaner visualization
        const periodStartDate = new Date(startDate)
        const getWeekKey = (dateStr) => {
          const date = new Date(dateStr)
          const startOfWeek = new Date(date)
          startOfWeek.setDate(date.getDate() - date.getDay() + 1) // Monday
          // Ensure week key doesn't go before the period start
          if (startOfWeek < periodStartDate) {
            return startDate // Use period start date instead
          }
          return toLocalDateString(startOfWeek)
        }

        // Add predictions (always add if available)
        if (hasPredictions) {
          setDataType(isLongPeriod && historicalData.length > 0 ? 'combined' : 'predicted')
          for (const suggestion of predictions) {
            const date = isLongPeriod ? getWeekKey(suggestion.suggested_date) : suggestion.suggested_date
            const strain = suggestion.product?.strain || 'Unknown'
            const qty = suggestion.suggested_quantity || 0

            if (!byDateAndProduct[date]) {
              byDateAndProduct[date] = { date, isPredicted: true }
            }
            byDateAndProduct[date][strain] = (byDateAndProduct[date][strain] || 0) + qty
            byDateAndProduct[date].isPredicted = true
            allProducts.add(strain)
          }
        }

        // For long periods, also add historical data for dates beyond predictions
        if (isLongPeriod && historicalData.length > 0) {
          if (!hasPredictions) setDataType('historical')

          for (const sale of historicalData) {
            // Shift historical date to current period
            const historicalDate = parseDateString(sale.date)
            const shiftedDate = new Date(historicalDate)
            shiftedDate.setFullYear(shiftedDate.getFullYear() + 1)
            const shiftedDateStr = toLocalDateString(shiftedDate)
            const date = isLongPeriod ? getWeekKey(shiftedDateStr) : shiftedDateStr

            // Skip if we already have prediction data for this date/week
            if (byDateAndProduct[date]?.isPredicted) continue

            for (const item of sale.sale_items || []) {
              const strain = item.product?.strain || 'Unknown'
              const qty = item.quantity || 0

              if (!byDateAndProduct[date]) {
                byDateAndProduct[date] = { date, isPredicted: false }
              }
              byDateAndProduct[date][strain] = (byDateAndProduct[date][strain] || 0) + qty
              allProducts.add(strain)
            }
          }
        }

        // Format dates for display
        const chartData = Object.values(byDateAndProduct)
          .sort((a, b) => a.date.localeCompare(b.date))
          .map(d => {
            const dateObj = new Date(d.date)
            let dateLabel
            if (isLongPeriod) {
              // Show week starting date
              dateLabel = `w/c ${dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
            } else {
              dateLabel = dateObj.toLocaleDateString('en-GB', {
                weekday: 'short',
                day: 'numeric',
                month: 'short'
              })
            }
            return { ...d, dateLabel }
          })

        setData(chartData)
        setProducts(Array.from(allProducts))
      } catch (error) {
        console.error('Error loading stacked demand data:', error)
        setData([])
        setProducts([])
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [period, offset])

  const isLoading = externalLoading !== undefined ? externalLoading : loading

  if (isLoading) {
    return (
      <div className="reports-chart-container">
        <div className="reports-loading">
          Loading forecast data...
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="reports-chart-container">
        <div className="reports-loading">
          No forecast data available for this period
        </div>
      </div>
    )
  }

  // Show indicator for data source
  const showDataSourceBanner = dataType === 'historical' || dataType === 'combined'

  // Assign colours to products
  const getProductColour = (product, index) => {
    return PRODUCT_COLOURS[product] || FALLBACK_COLOURS[index % FALLBACK_COLOURS.length]
  }

  return (
    <div className="reports-chart-container">
      {showDataSourceBanner && (
        <div className="text-xs text-muted-foreground bg-muted/50 px-3 py-1.5 rounded-md mb-2 inline-block">
          {dataType === 'combined'
            ? 'Predictions for first 2 weeks + historical baseline for remaining period (aggregated by week)'
            : 'Historical data from same period last year (aggregated by week)'
          }
        </div>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
          <XAxis
            dataKey="dateLabel"
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: 11,
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            angle={-45}
            textAnchor="end"
            height={60}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{
              fontSize: 12,
              fill: 'var(--muted-foreground)',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
            label={{
              value: 'Volume (kg)',
              angle: -90,
              position: 'insideLeft',
              style: { textAnchor: 'middle', fontSize: '12px', fill: 'var(--muted-foreground)' }
            }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            wrapperStyle={{
              paddingTop: '20px',
              fontSize: '12px',
              fontFamily: 'Geist, -apple-system, BlinkMacSystemFont, sans-serif'
            }}
          />
          {products.map((product, index) => (
            <Bar
              key={product}
              dataKey={product}
              name={product}
              stackId="a"
              fill={getProductColour(product, index)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  )
}
